import React, { useState, useEffect, useContext } from 'react';
import * as Sentry from '@sentry/react';

import { Input, Button, Card, CardHeader, CardBody, CardTitle, FormGroup, Label, Row, Col, UncontrolledTooltip } from 'reactstrap';

import toast from 'react-hot-toast';

import { setting, member, guestDocument } from '../../api';
import UserContext from '../UserContext';
import { FaSpinner, FaInfo, FaExclamationTriangle } from 'react-icons/fa';

import { SettingsClearancesType } from 'api/settings/clearances';
import { MemberDB, MemberHeaderDB } from 'api/members';
import { GuestDocumentDB } from 'api/guest-documents';
import { MemberDocumentDB } from 'api/members/document';
import { InvalidParametersException } from 'api/exceptions/InvalidParametersException';
import { FailedToFetchException } from 'api/exceptions/FailedToFetchException';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { ForbiddenException } from 'api/exceptions/ForbiddenException';
import { GuestDocumentMemberSearch } from './partials/GuestDocumentMemberSearch';
import { ModalOverlay } from 'components/Modal/Modal';

interface Props {}

export const GuestDocumentDetails = (props: Props) => {
	const { jwt } = useContext(UserContext);
	let navigate = useNavigate();
	const { docID } = useParams<{ docID: string }>();

	const [isLoading, setIsLoading] = useState(true);
	const [clearanceHeaders, setClearanceHeaders] = useState<Array<SettingsClearancesType>>([]);
	const [memberHeaders, setMemberHeaders] = useState<{ [key: string]: MemberHeaderDB }>({});
	const [data, setData] = useState<{
		documentData: GuestDocumentDB;
		docURL: string;
	} | null>(null);

	const [memberDocumentData, setMemberDocumentData] = useState<MemberDocumentDB | null>(null);
	const [canSave, setCanSave] = useState(false);
	const [formSaveInProgress, setFormSaveInProgress] = useState(false);

	const [selectedMember, setSelectedMember] = useState<MemberDB | null>(null);
	const [isMemberSearchModalOpen, setIsMemberSearchModalOpen] = useState(false);
	const [isSaveConfirmModalOpen, setIsSaveConfirmModalOpen] = useState(false);
	const [isUnarchiveMemberModalOpen, setIsUnarchiveMemberModalOpen] = useState(false);

	useEffect(() => {
		const getContents = async () => {
			try {
				const clearanceData = await setting.clearances.get(jwt);
				setClearanceHeaders(clearanceData.types_list);

				const listResp = await guestDocument.list(jwt);
				const selectedGuestDocument = listResp.filter((doc) => doc.docID === docID)[0];

				if (selectedGuestDocument == null) {
					throw new InvalidParametersException('Document not found');
				}

				const responseObj = await guestDocument.getDocument(jwt, docID ?? '');

				setData({ documentData: selectedGuestDocument, docURL: responseObj.docURL });
				setMemberDocumentData({ type: selectedGuestDocument.type } as MemberDocumentDB);

				setIsLoading(false);
			} catch (error) {
				if (error instanceof ForbiddenException) {
					toast.error(error.message);
				} else if (error instanceof InvalidParametersException) {
					toast.error(error.message);
				} else if (error instanceof FailedToFetchException) {
					toast.error(error.message);
				} else {
					Sentry.captureException(error);
					toast.error('Failed to load');
				}
			}
		};

		getContents();
	}, []);

	useEffect(() => {
		let canSave = true;

		if (memberDocumentData == null) {
			return;
		}

		// Validate the member
		if (selectedMember == null) {
			canSave = false;
		}

		// Block upload to archived members
		if (selectedMember?.archived != null) {
			canSave = false;
		}

		// Validate the member document
		if (memberDocumentData.type == null || memberDocumentData.certification_date == null) {
			canSave = false;
		}

		if (!clearanceHeaders.map((clearance) => clearance.uid).includes(memberDocumentData.type)) {
			canSave = false;
		}

		const todaysDate = new Date();
		todaysDate.setHours(0, 0, 0, 0);

		if (todaysDate.getTime() < new Date(memberDocumentData.certification_date).getTime()) {
			canSave = false;
		}

		setCanSave(canSave);
	}, [selectedMember, memberDocumentData]);

	const handleMemberSelectClick = (member: MemberDB) => {
		setSelectedMember(member);
		setIsMemberSearchModalOpen(false);
		if (member.archived != null) {
			setIsUnarchiveMemberModalOpen(true);
		}
	};

	const handleUnarchiveMemberClick = async () => {
		try {
			// Unarchive the member
			await member.restore(jwt, selectedMember!.docID!);

			// Refresh the member data
			const updatedMember = await member.get(jwt, selectedMember!.docID!);
			setSelectedMember(updatedMember);

			// Close the modal
			setIsUnarchiveMemberModalOpen(false);
			toast.success('Member unarchived');
		} catch (error) {
			if (error instanceof InvalidParametersException) {
				toast.error(error.message);
			} else if (error instanceof FailedToFetchException) {
				toast.error(error.message);
			} else {
				Sentry.captureException(error);
				toast.error('Failed to unarchive member');
			}
		}
	};

	const handleDocEditInputFieldChange = async (fieldName: keyof MemberDocumentDB, newValue: any) => {
		setMemberDocumentData((prev) => ({ ...prev, [fieldName]: newValue } as MemberDocumentDB));
	};

	const handleOnSaveClick = async () => {
		try {
			await guestDocument.putDocumentData(jwt, data?.documentData.docID ?? '', {
				memberDocID: selectedMember!.docID!,
				type: memberDocumentData!.type!,
				certificationDate: memberDocumentData!.certification_date!,
			});
			setIsSaveConfirmModalOpen(false);
			toast.success('Added document to member');

			navigate({
				pathname: generatePath('../', {}),
			});
		} catch (error) {
			if (error instanceof InvalidParametersException) {
				toast.error(error.message);
			} else if (error instanceof FailedToFetchException) {
				toast.error(error.message);
			} else {
				console.error(error);
				toast.error('Failed to save document');
			}

			setIsSaveConfirmModalOpen(false);
		}
	};

	const handleCancelClick = async () => {
		navigate({
			pathname: generatePath('../', {}),
		});
	};

	return (
		<div className="content guest-documents">
			<Row>
				<Col md="12">
					<Card>
						<CardHeader>
							<CardTitle tag="h4">Guest Document</CardTitle>
						</CardHeader>
						<CardBody>
							<Row>
								{isLoading ? (
									<Col md={12}>Loading...</Col>
								) : (
									<>
										{data != null ? (
											<>
												<Col md={8}>
													<h6>Uploaded Document</h6>

													{data.docURL != null ? (
														<>
															{data.docURL != null ? (
																<iframe
																	title={'guest-documents-frame'}
																	style={{ border: '0px' }}
																	src={`${data.docURL}`}
																></iframe>
															) : (
																<iframe
																	title={'guest-documents-frame'}
																	style={{ border: '0px' }}
																	src={`data:application/pdf;base64`}
																></iframe>
															)}
														</>
													) : (
														<div>No document available to view</div>
													)}
												</Col>
												<Col className={'information-data'} md={4}>
													<Row>
														<Col className={'align-self-center'}>
															<h6 className="m-0">Member Information</h6>
														</Col>
													</Row>
													<Row>
														<Col>
															{selectedMember ? (
																Object.keys(memberHeaders).map((header) => {
																	const obj = memberHeaders[header];

																	let val = selectedMember[header];

																	// if (header === 'uid') {
																	// 	val = selectedDocument.memberData[selectedDocument.memberData['uid']];
																	// }

																	return (
																		<FormGroup row key={`guest-document-user-info-${header}`}>
																			<Label sm={4}>
																				<strong>{(obj as any).name}</strong>
																			</Label>
																			<Col className={'data-value'} sm={8}>
																				{val}
																			</Col>
																		</FormGroup>
																	);
																})
															) : (
																<Button color="secondary" onClick={() => setIsMemberSearchModalOpen(true)} disabled={isLoading}>
																	Member Search
																</Button>
															)}
														</Col>
													</Row>
													<Row>
														<Col className={'align-self-center'}>
															<h6 className="m-0">Clearance Data</h6>
														</Col>
													</Row>
													<Row>
														<Col>
															<FormGroup row key={`guest-document-form-data-uploaded-date`}>
																<Label sm={4}>
																	<strong>Uploaded Date</strong>
																</Label>
																<Col className={'data-value'} sm={8}>
																	{`${String(data.documentData.uploaded_date.getMonth() + 1).padStart(2, '0')}-${String(
																		data.documentData.uploaded_date.getDate(),
																	).padStart(2, '0')}-${data.documentData.uploaded_date.getFullYear()}`}
																</Col>
															</FormGroup>
															<FormGroup row key={`guest-document-form-data-email-address`}>
																<Label sm={4}>
																	<strong>Email Address</strong>
																</Label>
																<Col className={'data-value'} sm={8}>
																	{data.documentData.emailAddress}
																</Col>
															</FormGroup>
															<FormGroup row key={`guest-document-form-data-type`}>
																<Label sm={4}>
																	<strong>Type</strong>
																</Label>
																<Col className={'data-value'} sm={5}>
																	<Input
																		type={'select'}
																		onChange={(e) => {
																			handleDocEditInputFieldChange('type', e.target.value);
																		}}
																		value={memberDocumentData?.type ?? ''}
																	>
																		<option>- Select Clearance -</option>
																		{clearanceHeaders.map((option) => {
																			if (option.attributes.readonly) {
																				return null;
																			}

																			return (
																				<option key={option.uid} value={option.uid}>
																					{option.name}
																				</option>
																			);
																		})}
																	</Input>
																</Col>
															</FormGroup>
															<FormGroup row key={`guest-document-form-data-certification_date`}>
																<Label sm={4}>
																	<strong>Certification Date</strong>
																</Label>
																<Col className={'data-value'} sm={5}>
																	<Input
																		type={'date'}
																		onChange={(e) => {
																			handleDocEditInputFieldChange('certification_date', e.target.value);
																		}}
																	/>
																</Col>
															</FormGroup>
														</Col>
													</Row>
													<Row>
														<Col>
															<Button className="m-0 pull-right" color="secondary" onClick={() => handleCancelClick()}>
																Cancel
															</Button>
															<Button
																className="m-0"
																color="primary"
																disabled={!canSave || formSaveInProgress}
																onClick={() => setIsSaveConfirmModalOpen(true)}
															>
																{formSaveInProgress ? <FaSpinner size={12} className="fa-spin" /> : 'Save to Member'}
															</Button>
														</Col>
													</Row>
												</Col>
											</>
										) : (
											<Col md={12}>No documents to review</Col>
										)}
									</>
								)}
							</Row>
						</CardBody>
					</Card>
				</Col>
			</Row>
			<GuestDocumentMemberSearch
				isOpen={isMemberSearchModalOpen}
				handleCloseClick={() => setIsMemberSearchModalOpen(!isMemberSearchModalOpen)}
				handleMemberSelectClick={handleMemberSelectClick}
			/>
			<ModalOverlay
				show={isSaveConfirmModalOpen}
				icon={FaInfo}
				title="Save Document to Member"
				primaryActionCallback={() => handleOnSaveClick()}
				primaryActionText="Yes, Save"
				secondaryActionCallback={() => setIsSaveConfirmModalOpen(false)}
			>
				<p>Are you sure you want to save the document to this member? This action cannot be undone. </p>
				<p>
					The <i>Member Document Approved</i> notification will be sent.
				</p>
			</ModalOverlay>
			<ModalOverlay
				show={isUnarchiveMemberModalOpen}
				icon={FaExclamationTriangle}
				iconColor="warning"
				title="Member is Archived"
				primaryActionCallback={handleUnarchiveMemberClick}
				primaryActionText="Unarchive Member"
				secondaryActionCallback={() => {
					setIsUnarchiveMemberModalOpen(false);
					setSelectedMember(null);
				}}
			>
				<p>This member is archived and cannot receive documents. Would you like to unarchive them?</p>
			</ModalOverlay>
		</div>
	);
};
